import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import QuoteForm from '../../../components/feature/QuoteForm';

export default function ConcreteDriveway() {
  const services = [
    {
      title: 'New Concrete Driveways',
      description: 'Custom concrete driveway installation with proper base preparation and finishing.',
      icon: 'ri-road-map-line',
    },
    {
      title: 'Stamped Concrete',
      description: 'Decorative stamped patterns that mimic stone, brick, or custom designs.',
      icon: 'ri-layout-grid-line',
    },
    {
      title: 'Colored Concrete',
      description: 'Integral color or stained finishes for unique, attractive driveways.',
      icon: 'ri-palette-line',
    },
    {
      title: 'Concrete Repair',
      description: 'Crack repair, slab replacement, and surface restoration services.',
      icon: 'ri-hammer-line',
    },
    {
      title: 'Concrete Resurfacing',
      description: 'Overlay worn concrete with new surface for refreshed appearance.',
      icon: 'ri-refresh-line',
    },
    {
      title: 'Sidewalks & Patios',
      description: 'Complete concrete services for walkways, patios, and outdoor spaces.',
      icon: 'ri-footprint-line',
    },
  ];

  const faqs = [
    {
      question: 'How much does a concrete driveway cost in Lincoln NE?',
      answer: 'Concrete driveways cost $6-$12 per square foot installed. A standard 20x20 (400 sq ft) driveway costs $2,400-$4,800. Stamped or colored concrete costs $10-$18 per square foot. Prices vary based on site conditions, thickness, finish, and decorative options. We provide detailed estimates after site inspection.',
    },
    {
      question: 'Is concrete or asphalt better for driveways in Nebraska?',
      answer: 'Both have advantages. Concrete lasts 30-40 years vs 15-20 for asphalt, requires less maintenance, and handles heat better. Asphalt costs less upfront ($3-$7/sq ft), repairs easier, and performs better in freeze-thaw cycles. We help you choose based on budget, aesthetics, and long-term goals.',
    },
    {
      question: 'How thick should a concrete driveway be?',
      answer: 'Standard residential driveways need 4 inches of concrete over 4-6 inches of compacted gravel base. Heavy vehicles require 5-6 inches. We use 3,000-4,000 PSI concrete with fiber reinforcement. Proper thickness and base preparation prevent cracking and ensure 30+ year lifespan.',
    },
    {
      question: 'How long before I can drive on new concrete?',
      answer: 'Wait 7 days before driving on new concrete, though it reaches 50% strength in 3 days. Full strength takes 28 days. We recommend keeping heavy vehicles off for 14 days. Proper curing is critical - we keep concrete moist for 7 days to prevent cracking and maximize strength.',
    },
    {
      question: 'Can you repair cracked concrete driveways?',
      answer: 'Yes! We repair hairline cracks with epoxy injection, wider cracks with routing and sealing, and replace severely damaged sections. If cracks are structural or widespread, replacement may be more cost-effective. We assess your concrete and recommend repair or replacement based on condition.',
    },
    {
      question: 'What is stamped concrete and how much does it cost?',
      answer: 'Stamped concrete uses patterns and textures to mimic stone, brick, slate, or wood. We pour concrete, apply color, stamp the pattern, and seal for protection. Stamped concrete costs $10-$18 per square foot, less than natural stone ($15-$30) with similar appearance. It lasts 25+ years with proper maintenance.',
    },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-primary-500 text-sm mb-4 tracking-wider">Services / Concrete</p>
            <h1 className="text-5xl md:text-6xl font-black mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Concrete Driveway Services
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Professional concrete driveway installation, repair, and stamped concrete in Lincoln, Nebraska
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div>
              <img
                src="https://static.readdy.ai/image/cd984ab7ba08a0f8b6f75adc7751930e/2b1fdb7464275cb4a6994004256af078.jpeg"
                alt="Lincoln NE concrete driveway installation"
                className="w-full h-96 object-cover rounded-2xl shadow-xl"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Beautiful, Durable Concrete Driveways
              </h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Concrete driveways offer unmatched durability, low maintenance, and design versatility. With proper installation, your concrete driveway will last 30-40 years while enhancing your home's curb appeal and value.
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                We specialize in custom concrete driveways, stamped concrete, colored finishes, and decorative options that transform ordinary driveways into stunning focal points. Our experienced crews deliver quality craftsmanship using premium materials and proven techniques.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Serving Lincoln and Lancaster County homeowners for over 20 years, we provide complete concrete services from design consultation to final sealing. Every project includes proper base preparation, reinforcement, and finishing for lasting beauty and performance.
              </p>
              <a href="tel:3084705637" className="inline-block bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-primary-700 transition-colors cursor-pointer">
                GET FREE ESTIMATE
              </a>
            </div>
          </div>

          {/* Services Grid */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Concrete Services
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-2xl border-2 border-gray-200 hover:border-primary-600 transition-all">
                  <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mb-6">
                    <i className={`${service.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {service.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Detailed Content */}
          <div className="prose prose-lg max-w-none mb-16">
            <h2 className="text-4xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Concrete Driveway Installation in Lincoln NE
            </h2>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Concrete driveways are a premium choice for Lincoln homeowners who want durability, low maintenance, and design flexibility. Unlike asphalt that requires regular sealcoating, concrete needs minimal maintenance and lasts decades. The initial investment pays off through longevity and reduced upkeep costs.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Our concrete driveway installation process ensures quality results that withstand Nebraska's harsh climate. We handle every phase: site preparation, base installation, forming, pouring, finishing, and curing. Attention to detail at each step prevents common problems like cracking, settling, and surface deterioration.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              New Concrete Driveway Installation
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              New concrete driveways start with thorough site preparation. We excavate to proper depth (typically 8-10 inches), grade for drainage away from buildings, and compact the subgrade to prevent settling. Poor base preparation is the leading cause of concrete failure.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We install 4-6 inches of compacted gravel base for drainage and stability. In areas with poor soil or high water tables, we use geotextile fabric to prevent base migration. The base is compacted in lifts to 95% density, creating a stable foundation that won't settle.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Concrete is poured 4 inches thick for residential driveways (5-6 inches for heavy vehicles). We use 3,000-4,000 PSI concrete with fiber reinforcement for crack resistance. Control joints are cut every 10 feet to control cracking. Proper finishing creates a smooth, durable surface with slight texture for traction.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Stamped Concrete Driveways
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Stamped concrete transforms ordinary driveways into stunning architectural features. We offer dozens of patterns: cobblestone, slate, brick, flagstone, wood plank, and custom designs. Stamped concrete provides the beauty of natural materials at a fraction of the cost.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              The stamping process involves pouring concrete, applying color hardener, stamping the pattern while concrete is workable, and sealing after curing. We use professional stamps and techniques to create realistic textures and patterns. Color options include earth tones, grays, reds, and custom blends.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Stamped concrete costs $10-$18 per square foot, compared to $15-$30 for natural stone. It lasts 25+ years with proper maintenance (resealing every 2-3 years). Stamped driveways are popular in Lincoln's upscale neighborhoods for their beauty and durability.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Colored Concrete Options
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Colored concrete adds visual interest without the cost of stamping. We offer two coloring methods: integral color (mixed into concrete) and acid staining (applied after curing). Integral color provides consistent, fade-resistant color throughout the concrete.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Popular colors include earth tones (tan, brown, terra cotta), grays (charcoal, slate, pewter), and custom blends. Colored concrete costs $8-$12 per square foot, adding $2-$4 per square foot to standard concrete prices. The result is a unique driveway that complements your home's architecture.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Concrete Driveway Repair
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Even quality concrete develops cracks over time. We repair hairline cracks with epoxy injection, wider cracks with routing and sealing, and structural cracks with stitching or replacement. Early repair prevents water infiltration that causes further damage.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              For severely damaged sections, we saw cut and replace individual slabs. This is more cost-effective than full replacement when damage is localized. We match existing concrete color and finish for seamless repairs. Replaced sections last as long as the original driveway.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Sunken or settled concrete can be raised with mudjacking or polyurethane foam injection. These techniques lift concrete back to proper grade at 50-70% the cost of replacement. We assess your concrete and recommend the most cost-effective repair solution.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Concrete Resurfacing
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Worn, stained, or outdated concrete can be resurfaced with a new overlay. We apply a 1/4 to 1/2 inch layer of polymer-modified concrete over the existing surface, creating a fresh, attractive finish. Resurfacing costs $3-$8 per square foot, much less than replacement.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Resurfacing options include smooth finishes, stamped patterns, stained colors, and decorative aggregates. The overlay bonds to existing concrete and lasts 10-15 years. It's ideal for driveways with surface wear but sound structural condition.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Concrete vs Asphalt Driveways
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Choosing between concrete and asphalt depends on budget, aesthetics, and maintenance preferences. Concrete costs more upfront ($6-$12/sq ft vs $3-$7 for asphalt) but lasts twice as long (30-40 years vs 15-20). Concrete requires minimal maintenance while asphalt needs sealcoating every 2-3 years.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Concrete handles heat better and won't soften in summer sun. It offers more design options with stamping, coloring, and decorative finishes. Asphalt repairs easier and performs better in freeze-thaw cycles. We help you evaluate both options based on your priorities and budget.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Maintenance and Care
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Concrete driveways require minimal maintenance. Seal every 2-3 years to protect against moisture, stains, and freeze-thaw damage. Clean with pressure washing or mild detergent. Avoid deicing salts that can damage concrete - use sand or calcium chloride instead.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Repair cracks promptly to prevent water infiltration. Keep expansion joints clean and flexible. With proper care, your concrete driveway will provide decades of service while maintaining its attractive appearance.
            </p>
          </div>

          {/* Gallery */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Concrete Driveway Gallery
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <img
                src="https://public.readdy.ai/ai/img_res/502e09f0da397743ddd3bf17aed46b77.jpg"
                alt="Stamped concrete driveway"
                className="w-full h-64 object-cover rounded-2xl shadow-xl"
              />
              <img
                src="https://readdy.ai/api/search-image?query=smooth%20colored%20concrete%20driveway%20with%20gray%20finish%20and%20clean%20lines%20for%20modern%20home%20in%20Lincoln%20Nebraska%20with%20clean%20simple%20light%20background&width=600&height=400&seq=41&orientation=landscape"
                alt="Colored concrete driveway"
                className="w-full h-64 object-cover rounded-2xl shadow-xl"
              />
              <img
                src="https://readdy.ai/api/search-image?query=decorative%20concrete%20driveway%20with%20exposed%20aggregate%20finish%20showing%20textured%20surface%20for%20residential%20property%20in%20Lincoln%20Nebraska%20with%20clean%20simple%20light%20background&width=600&height=400&seq=42&orientation=landscape"
                alt="Decorative concrete"
                className="w-full h-64 object-cover rounded-2xl shadow-xl"
              />
            </div>
          </div>

          {/* FAQs */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-2xl border-2 border-gray-200">
                  <h3 className="text-2xl font-bold mb-4 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {faq.question}
                  </h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Quote Form */}
          <div className="bg-gray-50 p-12 rounded-2xl">
            <h2 className="text-4xl font-bold mb-6 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Request Your Free Quote
            </h2>
            <p className="text-xl text-gray-700 text-center mb-8">
              Get expert concrete driveway installation and repair services
            </p>
            <QuoteForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
